import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { scaffolderTemplateGroups } from './components/scaffolder/scaffolderTemplateGroups';
import { HiddenFieldExtension } from './components/scaffolder/HiddenFieldExtension';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { techdocsPlugin, TechDocsReaderPage } from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { appThemes } from './themes';
import { DocsHomePage } from './components/techdocs/DocsHomePage';
import { entityPage } from './components/catalog/EntityPage';
import { customCatalogColumns } from './components/catalog/CustomCatalogPage';
import { PsCatalogFilters } from './components/catalog/PsCatalogFilters';
import { ImagesCatalogPage } from './components/catalog/ImagesCatalogPage';
import { ImageBuildPickerFieldExtension } from './components/scaffolder/ImageBuildPickerField';
import { ImageBuildOverridesFieldExtension } from './components/scaffolder/ImageBuildOverridesField';
import { PortalManifestGuideFieldExtension } from './components/scaffolder/PortalManifestGuideField';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { AgentChatPage } from './components/agent/AgentChatPage';
import { RequirePsAgentAccess } from './components/agent/RequirePsAgentAccess';

import { AlertDisplay, OAuthRequestDialog } from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { AppSignInPage } from './components/AppSignInPage';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';

const app = createApp({
  apis,
  themes: appThemes,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: AppSignInPage,
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route
      path="/catalog"
      element={
        <CatalogIndexPage
          columns={customCatalogColumns}
          filters={<PsCatalogFilters initialScope="my" excludeImageBuilds />}
        />
      }
    />
    <Route path="/images" element={<ImagesCatalogPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<DocsHomePage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/agent"
      element={
        <RequirePsAgentAccess>
          <AgentChatPage />
        </RequirePsAgentAccess>
      }
    />
    <Route
      path="/create"
      element={
        <ScaffolderPage
          groups={scaffolderTemplateGroups}
          headerOptions={{
            title: 'Create infrastructure',
            subtitle:
              'Templates grouped by goal. Redis Cloud entries are network integrations — not greenfield Redis labs.',
          }}
        >
          <ScaffolderFieldExtensions>
            <HiddenFieldExtension />
            <ImageBuildPickerFieldExtension />
            <PortalManifestGuideFieldExtension />
            <ImageBuildOverridesFieldExtension />
          </ScaffolderFieldExtensions>
        </ScaffolderPage>
      }
    />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
